import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { addDays, diffDays, weekStart, formatFullDate, weekdayShort, dayOfMonth } from '@jungles/shared';
import { api, loadToken } from '../lib/api';
import './nutrition-days.css';

// A moving, bounded DOM window, not a limit on the user's diary history.
const WINDOW = 364;
const SHIFT = 91;
type Mark = { date: string; hasFood: boolean };
// Whole-week offsets include the same gap used between days. Measure the
// laid-out targets so recycling and selection share the browser's snap stride.
const dayStride = (el: HTMLElement) => {
  const first = el.children[0], next = el.children[1];
  return (first && next
    ? next.getBoundingClientRect().x - first.getBoundingClientRect().x
    : el.clientWidth) / 7;
};
export function NutritionDays({ today, selected, week, onSelect }: {
  today: string; selected: string; week: Mark[]; onSelect: (date: string) => void;
}) {
  const end = addDays(weekStart(today), 6);
  const rangeFor = (date: string) => {
    const desired = addDays(weekStart(date), -SHIFT * 2);
    return desired > addDays(end, 1 - WINDOW) ? addDays(end, 1 - WINDOW) : desired;
  };
  const [start, setStart] = useState(() => rangeFor(selected));
  const strip = useRef<HTMLElement>(null);
  const previousStart = useRef(start);
  const pendingScroll = useRef<number | null>(null);
  const previousSelected = useRef<string | null>(null);
  const focused = useRef<string | null>(null);
  const gesture = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const ready = useRef(false);
  const marks = useRef(new Map<string, boolean>());
  const [visibleWeek, setVisibleWeek] = useState(() => weekStart(selected));
  const [, redrawMarks] = useState(0);
  // This component is mounted beneath NutritionContent's account key. Requests
  // live across swipes, not across logout/account changes or unmounts.
  const weeksCache = useRef(new Map<string, { updated: number; request?: AbortController }>());
  const count = Math.min(WINDOW, diffDays(start, end) + 1);
  const dates = Array.from({ length: Math.max(0, count) }, (_, i) => addDays(start, i));
  useLayoutEffect(() => {
    // A newly returned diary week (including save/delete/refresh) supersedes an
    // older prefetch of the same week. Never reapply an old prop on every render.
    const keys = new Set(week.map(item => weekStart(item.date)));
    for (const key of keys) {
      weeksCache.current.get(key)?.request?.abort();
      weeksCache.current.set(key, { updated: Date.now() });
    }
    for (const item of week) marks.current.set(item.date, item.hasFood);
    redrawMarks(value => value + 1);
  }, [week]);
  useEffect(() => {
    const cache = weeksCache.current;
    // One behind the shown week, plus the next week for a return swipe. No
    // recursive historical loading; civil-date helpers avoid timezone shifts.
    const wanted = [visibleWeek, addDays(visibleWeek, -7), addDays(visibleWeek, 7)].filter(key => key <= weekStart(today));
    for (const key of wanted) {
      const old = cache.get(key);
      cache.delete(key); // touch bounded LRU, including in-flight weeks
      const entry = old ?? { updated: 0 };
      cache.set(key, entry);
      const complete = Array.from({ length: 7 }, (_, i) => addDays(key, i)).every(date => marks.current.has(date));
      if (entry.request || (complete && Date.now() - entry.updated < 60_000)) continue;
      const controller = new AbortController(), token = loadToken();
      entry.request = controller;
      api.get<{ week: Mark[] }>(`/api/nutrition/day?date=${key}`, controller.signal).then(data => {
        if (controller.signal.aborted || token !== loadToken() || cache.get(key) !== entry) return;
        for (const item of data.week) {
          if (item.date >= key && item.date <= addDays(key, 6)) marks.current.set(item.date, item.hasFood);
        }
        entry.updated = Date.now();
        redrawMarks(value => value + 1);
      }).catch(() => { /* Preserve known marks; unknown is not empty. Retry on return. */ })
        .finally(() => { if (entry.request === controller) entry.request = undefined; });
    }
    while (cache.size > 8) {
      const key = cache.keys().next().value!;
      cache.get(key)?.request?.abort();
      cache.delete(key);
      for (let i = 0; i < 7; i++) marks.current.delete(addDays(key, i));
    }
  }, [visibleWeek, week, today]);
  useEffect(() => () => {
    for (const entry of weeksCache.current.values()) entry.request?.abort();
    weeksCache.current.clear();
    marks.current.clear();
  }, []);
  const reportVisibleWeek = (el: HTMLElement) => {
    const stride = dayStride(el);
    if (el.clientWidth <= 0 || !Number.isFinite(stride) || stride <= 0) return;
    const index = Math.max(0, Math.min(dates.length - 1, Math.floor((el.scrollLeft + el.clientWidth / 2) / stride)));
    setVisibleWeek(weekStart(dates[index]!));
  };
  useLayoutEffect(() => {
    const el = strip.current;
    if (!el) return;
    const stride = dayStride(el);
    // Preserve the exact visible date/pixel anchor when recycling either edge.
    if (previousStart.current !== start) {
      // Snap layout may already have re-anchored the retained Monday. Do not
      // add the window shift to that browser-adjusted offset a second time.
      if (pendingScroll.current !== null) el.scrollLeft = pendingScroll.current;
      pendingScroll.current = null;
      previousStart.current = start;
    }
    if (previousSelected.current !== selected) {
      if (selected < start || selected > dates[dates.length - 1]!) {
        setStart(rangeFor(selected));
        return;
      }
      const offset = diffDays(start, selected) * stride;
      if (!ready.current || offset < el.scrollLeft || offset + stride - 5 > el.scrollLeft + el.clientWidth) {
        el.scrollLeft = diffDays(start, weekStart(selected)) * stride;
      }
      previousSelected.current = selected;
      ready.current = true;
    }
    if (focused.current) {
      const target = el.querySelector<HTMLButtonElement>(`[data-date="${focused.current}"]`);
      if (target) { target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); focused.current = null; }
    }
    reportVisibleWeek(el);
  });
  const rewindow = () => {
    const el = strip.current;
    if (!el || !ready.current) return;
    reportVisibleWeek(el);
    const stride = dayStride(el);
    const moveWindow = (next: string) => {
      pendingScroll.current = el.scrollLeft + diffDays(next, start) * stride;
      setStart(next);
    };
    if (el.scrollLeft < stride * 21) moveWindow(addDays(start, -SHIFT));
    else if (el.scrollWidth - el.clientWidth - el.scrollLeft < stride * 21 && dates[dates.length - 1]! < end) {
      const next = addDays(start, SHIFT), latest = addDays(end, 1 - WINDOW);
      moveWindow(next > latest ? latest : next);
    }
  };
  const weeks = Array.from({ length: Math.ceil(dates.length / 7) }, (_, i) => dates.slice(i * 7, i * 7 + 7));
  return <nav ref={strip} className="wkn nutrition-days" aria-label="День недели" tabIndex={0}
    aria-description="Прокрутите дни и выберите дату. Стрелки клавиатуры перемещают фокус, Enter выбирает день."
    onScroll={rewindow}
    onKeyDown={event => {
      if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const el = event.currentTarget;
      const stride = dayStride(el);
      const visibleDate = dates[Math.min(dates.length - 1, Math.round(el.scrollLeft / stride))] ?? selected;
      const date = (event.target as HTMLElement).dataset.date ?? visibleDate;
      const next = addDays(date, event.key === 'ArrowLeft' ? -1 : 1);
      if (next > today) return;
      const target = strip.current?.querySelector<HTMLButtonElement>(`[data-date="${next}"]`);
      if (target) { target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' }); }
      else { focused.current = next; setStart(rangeFor(next)); }
    }}
    onTouchStart={event => {
      const t = event.touches[0];
      if (event.touches.length !== 1) { gesture.current = null; suppressClick.current = true; return; }
      suppressClick.current = false;
      gesture.current = t ? { x: t.clientX, y: t.clientY } : null;
    }}
    onTouchMove={event => {
      const t = event.touches[0], origin = gesture.current;
      if (event.touches.length !== 1 || (t && origin && Math.hypot(t.clientX - origin.x, t.clientY - origin.y) > 8)) suppressClick.current = true;
    }}
    onTouchEnd={() => { gesture.current = null; }}
    onTouchCancel={() => { gesture.current = null; suppressClick.current = true; }}
    onClickCapture={event => {
      if (event.detail !== 0 && suppressClick.current) { event.preventDefault(); event.stopPropagation(); }
    }}>
    {weeks.map(days => <div className="nutrition-week" key={days[0]} data-week={days[0]}>
    {days.map(date => <button key={date} data-date={date} type="button"
      className={`dayn${date === selected ? ' on' : ''}${date > today ? ' fut' : ''}`}
      disabled={date > today} tabIndex={date === selected ? 0 : -1}
      aria-current={date === selected ? 'date' : undefined}
      aria-label={`${formatFullDate(date)}${date === today ? ', сегодня' : ''}`}
      onClick={() => onSelect(date)}>
      {weekdayShort(date)}<b>{dayOfMonth(date)}</b><i className={marks.current.get(date) ? 'food' : undefined} />
    </button>)}
    </div>)}
  </nav>;
}
